import { supabase } from "@/integrations/supabase/client";
import { OfferingCreateData, OfferingUpdateData, MentorshipOffering, OrderUpdate } from "@/types/mentorOffering";
import { notifyMentorshipCreation } from "./notificationService";

/**
 * Create a new mentorship offering for the current mentor
 */
export async function createMentorshipOffering(offeringData: OfferingCreateData): Promise<MentorshipOffering> {
  console.log("📊 [createMentorshipOffering] Função iniciada com dados:", offeringData);
  
  try {
    console.log("📊 [createMentorshipOffering] Buscando dados do mentor...");
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", (await supabase.auth.getUser()).data.user?.id || "")
      .single();

    if (mentorError) {
      console.error("📊 [createMentorshipOffering] Erro ao buscar mentor:", mentorError);
      throw mentorError;
    }

    const mentorId = mentorData.id;
    console.log("📊 [createMentorshipOffering] Mentor ID encontrado:", mentorId);

    console.log("📊 [createMentorshipOffering] Inserindo mentoria no banco...");
    const { data, error } = await supabase
      .from("mentorship_offerings")
      .insert({
        ...offeringData,
        mentor_id: mentorId,
      })
      .select()
      .single();

    if (error) {
      console.error("📊 [createMentorshipOffering] Erro ao inserir no banco:", error);
      throw error;
    }
    
    console.log("📊 [createMentorshipOffering] Mentoria inserida com sucesso:", data);
    console.log("📊 [createMentorshipOffering] CHAMANDO NOTIFICAÇÃO DE EMAIL...");
    
    // Send email notification about the new offering
    await notifyMentorshipCreation(offeringData);
    
    console.log("📊 [createMentorshipOffering] Notificação de email processada (verificar logs do notificationService)");

    return data;
  } catch (error) {
    console.error("📊 [createMentorshipOffering] Erro geral:", error);
    throw error;
  }
}

/**
 * Get all mentorship offerings for the current mentor (apenas ativas por padrão)
 */
export async function getMentorshipOfferings(includeInactive: boolean = false): Promise<MentorshipOffering[]> {
  try {
    const { data: mentorData, error: mentorError } = await supabase
      .from("mentor_info")
      .select("id")
      .eq("profile_id", (await supabase.auth.getUser()).data.user?.id || "")
      .single();

    if (mentorError) {
      console.error("Error fetching mentor info:", mentorError);
      throw mentorError;
    }

    const mentorId = mentorData.id;

    let query = supabase
      .from("mentorship_offerings")
      .select(`
        id,
        title,
        description,
        price_cents,
        duration_minutes,
        is_active,
        is_group,
        max_participants,
        display_order,
        cover_image_url,
        game_id,
        mentor_id,
        created_at,
        game:game_id(id, name, cover_url),
        mentor:mentor_id(
          id,
          image_url,
          profile_id,
          profiles:profile_id(
            id,
            nickname
          )
        )
      `)
      .eq("mentor_id", mentorId);

    // Filtrar apenas ativas se não especificado o contrário
    if (!includeInactive) {
      query = query.eq("is_active", true);
    }

    const { data, error } = await query
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching mentorship offerings:", error);
      throw error;
    }

    // Map to include mentor name and image
    const mappedData = (data || []).map(item => ({
      ...item,
      mentor_name: item.mentor?.profiles?.nickname || 'Mentor',
      mentor_image_url: item.mentor?.image_url || null
    }));

    return mappedData;
  } catch (error) {
    console.error("Error in getMentorshipOfferings:", error);
    throw error;
  }
}

/**
 * Update an existing mentorship offering
 */
export async function updateMentorshipOffering(
  id: string,
  offeringData: OfferingUpdateData
): Promise<MentorshipOffering> {
  try {
    const { data, error } = await supabase
      .from("mentorship_offerings")
      .update(offeringData)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Error updating mentorship offering:", error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error in updateMentorshipOffering:", error);
    throw error;
  }
}

/**
 * Soft delete a mentorship offering (marca como inativa em vez de excluir quando há dependências)
 */
export async function deleteMentorshipOffering(id: string): Promise<boolean> {
  try {
    // Primeiro, verificar se existem sessões associadas
    const { count, error: countError } = await supabase
      .from("sessions")
      .select("*", { count: "exact", head: true })
      .or(`offering_id.eq.${id},mentorship_offering_id.eq.${id}`);

    if (countError) {
      console.error("Erro ao verificar sessões associadas:", countError);
      throw countError;
    }

    const sessionsCount = count || 0;

    // Se existem sessões, fazer soft delete (marcar como inativa)
    if (sessionsCount > 0) {
      console.log(`Mentoria ${id} possui ${sessionsCount} sessão(s) associada(s). Fazendo soft delete...`);
      
      const { error: updateError } = await supabase
        .from("mentorship_offerings")
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq("id", id);

      if (updateError) {
        console.error("Erro ao desativar mentoria:", updateError);
        throw updateError;
      }

      console.log(`Mentoria ${id} foi desativada com sucesso`);
      return true;
    }

    // Se não existem sessões, pode fazer delete físico
    const { error } = await supabase
      .from("mentorship_offerings")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting mentorship offering:", error);
      throw error;
    }

    console.log(`Mentoria ${id} foi excluída permanentemente`);
    return true;
  } catch (error) {
    console.error("Error in deleteMentorshipOffering:", error);
    throw error;
  }
}

/**
 * Verificar se uma mentoria pode ser excluída permanentemente
 */
export async function canDeleteMentorshipOffering(id: string): Promise<{
  canDelete: boolean;
  reason?: string;
  sessionsCount: number;
}> {
  try {
    const { count, error } = await supabase
      .from("sessions")
      .select("*", { count: "exact", head: true })
      .or(`offering_id.eq.${id},mentorship_offering_id.eq.${id}`);

    if (error) {
      console.error("Erro ao verificar dependências:", error);
      throw error;
    }

    const sessionsCount = count || 0;
    
    if (sessionsCount > 0) {
      return {
        canDelete: false,
        reason: `Esta mentoria possui ${sessionsCount} sessão(s) associada(s). Ela será desativada em vez de excluída.`,
        sessionsCount
      };
    }

    return {
      canDelete: true,
      sessionsCount
    };
  } catch (error) {
    console.error("Error in canDeleteMentorshipOffering:", error);
    throw error;
  }
}

/**
 * Update the display order of offerings by list of IDs
 */
export async function updateOfferingsOrder(orderedIds: string[]): Promise<boolean> {
  try {
    // Create transaction batch
    for (let i = 0; i < orderedIds.length; i++) {
      const { error } = await supabase
        .from("mentorship_offerings")
        .update({ display_order: i })
        .eq("id", orderedIds[i]);

      if (error) {
        console.error(`Error updating order for offering ${orderedIds[i]}:`, error);
        throw error;
      }
    }

    return true;
  } catch (error) {
    console.error("Error in updateOfferingsOrder:", error);
    throw error;
  }
}

/**
 * Update offerings display order by orderUpdates array
 */
export async function updateOfferingsDisplayOrder(orderUpdates: OrderUpdate[]): Promise<boolean> {
  try {
    // Create transaction batch for updating each offering's display order
    for (const update of orderUpdates) {
      const { error } = await supabase
        .from("mentorship_offerings")
        .update({ display_order: update.display_order })
        .eq("id", update.id);

      if (error) {
        console.error(`Error updating order for offering ${update.id}:`, error);
        throw error;
      }
    }

    return true;
  } catch (error) {
    console.error("Error in updateOfferingsDisplayOrder:", error);
    throw error;
  }
}
